/**
 * Parameter Control Components for Care360 Simulation Sandbox
 * 
 * Phase 3: UI Control Logic Implementation
 * Creates appropriate UI controls for different parameter types:
 * - Continuous parameters: Smooth sliders with ranges
 * - Categorical parameters: Dropdown menus or radio buttons
 * - Binary parameters: Toggle switches or checkboxes
 * - Derived parameters: Read-only calculated displays
 * - Ordinal parameters: Rating scales or stepped sliders
 */

import React, { useState, useCallback, useEffect } from 'react';
import { 
  Slider, 
  InputNumber, 
  Select, 
  Switch, 
  Radio, 
  Rate, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Tooltip, 
  Tag, 
  Alert,
  Progress,
  Statistic
} from 'antd';
import { 
  InfoCircleOutlined, 
  ThunderboltOutlined, 
  CalculatorOutlined,
  HeartOutlined,
  TrophyOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { PARAMETER_TYPES } from '../core/ParameterClassification';
import { getParameterDisplayName } from '../../utils/parameterDisplayNames';
import { calculateDynamicSliderRange, getSliderDefaultValue } from '../utils/DynamicRangeCalculator';
import { getHealthScoreColor } from '../../utils/healthColorUtils';

const { Text, Title } = Typography;
const { Option } = Select;

/**
 * Continuous Parameter Slider Component
 * For numeric parameters with defined ranges (e.g., blood pressure, glucose)
 * Enhanced with dynamic range calculation using HealthSnapshotV2 methodology
 */
export const ContinuousParameterControl = ({
  paramKey,
  config,
  value,
  onChange,
  cascadingChange = null,
  showEvidence = false,
  // New props for dynamic range calculation
  patientDemographics = null,
  targetFormulas = null,
  scoringConfig = null,
  patientData = null,
  // Enhanced props for Z-score based status
  dynamicRange: providedDynamicRange = null,
  parameterStatus = null,
  statusColor = '#d9d9d9'
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [dynamicRange, setDynamicRange] = useState(providedDynamicRange);
  const [rangeCalculationDetails, setRangeCalculationDetails] = useState(null);

  // Use provided dynamic range or calculate if not provided
  useEffect(() => {
    if (providedDynamicRange) {
      // Use the dynamic range calculated by the parent component
      setDynamicRange(providedDynamicRange);
      console.log('[ContinuousParameterControl] Using provided dynamic range for:', paramKey, providedDynamicRange);
    } else if (config.supportsDynamicRange && patientDemographics && targetFormulas && scoringConfig) {
      console.log('[ContinuousParameterControl] Calculating dynamic range for:', paramKey);

      try {
        const calculatedRange = calculateDynamicSliderRange(
          paramKey,
          patientDemographics,
          targetFormulas,
          scoringConfig,
          config.dynamicRangeConfig?.safetyBounds
        );

        setDynamicRange(calculatedRange);
        setRangeCalculationDetails(calculatedRange.calculationDetails);

        console.log('[ContinuousParameterControl] Dynamic range calculated:', calculatedRange);
      } catch (error) {
        console.error('[ContinuousParameterControl] Error calculating dynamic range:', error);
        setDynamicRange(null);
        setRangeCalculationDetails({ error: error.message });
      }
    } else {
      console.log('[ContinuousParameterControl] Using static range for:', paramKey, {
        supportsDynamicRange: config.supportsDynamicRange,
        hasPatientDemographics: !!patientDemographics,
        hasTargetFormulas: !!targetFormulas,
        hasScoringConfig: !!scoringConfig
      });
    }
  }, [paramKey, config, patientDemographics, targetFormulas, scoringConfig, providedDynamicRange]);

  // Update input value when dynamic range changes or when getting default value
  useEffect(() => {
    if (dynamicRange && (value === null || value === undefined)) {
      const defaultValue = getSliderDefaultValue(paramKey, patientData, dynamicRange);
      setInputValue(defaultValue);
      onChange(paramKey, defaultValue);
    }
  }, [dynamicRange, paramKey, patientData, value, onChange]);

  const handleSliderChange = useCallback((newValue) => {
    setInputValue(newValue);
    onChange(paramKey, newValue);
  }, [paramKey, onChange]);
  
  const handleInputChange = useCallback((newValue) => {
    const activeRange = dynamicRange || config.range;
    if (newValue >= activeRange.min && newValue <= activeRange.max) {
      setInputValue(newValue);
      onChange(paramKey, newValue);
    }
  }, [paramKey, config.range, dynamicRange, onChange]);
  
  const isAffected = !!cascadingChange;
  const normalRange = config.normalRange || {};
  const criticalLimits = config.criticalLimits || {};

  // Use dynamic range if available, otherwise fall back to static config range
  const activeRange = dynamicRange || config.range;
  const isUsingDynamicRange = !!dynamicRange;
  
  // Determine value status - use provided parameterStatus (Z-score based) or fallback to range-based
  const getValueStatus = (val) => {
    // If we have a Z-score based status from the parent component, use it
    if (parameterStatus) {
      return parameterStatus;
    }

    // Fallback to range-based status determination
    if (criticalLimits.min && val < criticalLimits.min) return 'critical-low';
    if (criticalLimits.max && val > criticalLimits.max) return 'critical-high';
    if (normalRange.min && val < normalRange.min) return 'low';
    if (normalRange.max && val > normalRange.max) return 'high';
    return 'normal';
  };

  const valueStatus = getValueStatus(value);
  // Use health spectrum colors for consistent status visualization
  const getStatusColor = (status) => {
    switch (status) {
      case 'critical':
      case 'critical-low':
      case 'critical-high':
        return getHealthScoreColor(30); // Red - critical health issues
      case 'high':
      case 'moderate':
        return getHealthScoreColor(65); // Yellow-orange - concerning
      case 'low':
        return getHealthScoreColor(75); // Yellow - moderate health
      case 'normal':
      default:
        return getHealthScoreColor(85); // Light green - good health
    }
  };

  const statusColors = {
    'critical-low': getStatusColor('critical'),
    'critical-high': getStatusColor('critical'),
    'critical': getStatusColor('critical'),
    'low': getStatusColor('low'),
    'high': getStatusColor('high'),
    'moderate': getStatusColor('moderate'),
    'normal': getStatusColor('normal')
  };
  
  // Create slider marks for key values with enhanced visual indicators
  const marks = {};

  if (isUsingDynamicRange && dynamicRange?.idealTarget) {
    // Enhanced ideal target indicator with more prominent styling
    const idealTargetValue = Math.round(dynamicRange.idealTarget * 10) / 10; // Round to 1 decimal
    marks[idealTargetValue] = {
      label: (
        <Tooltip title={`Ideal Target: ${idealTargetValue} ${config.unit}`}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            lineHeight: '1.2',
            background: '#fff7e6',
            padding: '4px 6px',
            borderRadius: '4px',
            border: '1px solid #ffd591',
            minWidth: '40px'
          }}>
            <TrophyOutlined style={{ color: '#fa8c16', fontSize: '14px' }} />
            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#d46b08' }}>Ideal</span>
            <span style={{ fontSize: '9px', color: '#d46b08' }}>{idealTargetValue}</span>
          </div>
        </Tooltip>
      ),
      style: {
        color: '#fa8c16',
        fontWeight: 'bold',
        transform: 'translateY(-10px)' // Lift the mark above the slider
      }
    };

    // Enhanced current value indicator with more prominent styling
    if (patientData !== null && patientData !== undefined && !isNaN(patientData)) {
      const currentValue = Math.round(patientData * 10) / 10; // Round to 1 decimal
      marks[currentValue] = {
        label: (
          <Tooltip title={`Your Current Value: ${currentValue} ${config.unit}`}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              lineHeight: '1.2',
              background: '#e6f7ff',
              padding: '4px 6px',
              borderRadius: '4px',
              border: '1px solid #91d5ff',
              minWidth: '40px'
            }}>
              <HeartOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
              <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#0050b3' }}>Current</span>
              <span style={{ fontSize: '9px', color: '#0050b3' }}>{currentValue}</span>
            </div>
          </Tooltip>
        ),
        style: {
          color: '#1890ff',
          fontWeight: 'bold',
          transform: 'translateY(-10px)' // Lift the mark above the slider
        }
      };
    }
  } else {
    // For static range, show normal and critical limits
    if (normalRange.min) marks[normalRange.min] = { label: 'Low Normal', style: { color: getStatusColor('low') } };
    if (normalRange.max) marks[normalRange.max] = { label: 'High Normal', style: { color: getStatusColor('high') } };
    if (criticalLimits.min) marks[criticalLimits.min] = { label: 'Critical', style: { color: getStatusColor('critical') } };
    if (criticalLimits.max) marks[criticalLimits.max] = { label: 'Critical', style: { color: getStatusColor('critical') } };
  }
  
  return (
    <Card 
      size="small" 
      style={{ 
        marginBottom: '16px',
        border: isAffected ? '2px solid #52c41a' : `2px solid ${statusColors[valueStatus]}`,
        backgroundColor: isAffected ? '#f6ffed' : 'white'
      }}
    >
      <Row gutter={[16, 8]} align="middle">
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col flex="auto">
              <Text strong style={{ color: statusColors[valueStatus] }}>
                {getParameterDisplayName(paramKey) || config.displayName || paramKey}
                {isUsingDynamicRange && (
                  <Tag color="green" size="small" style={{ marginLeft: '8px' }}>
                    Dynamic Range
                  </Tag>
                )}
                {parameterStatus && (
                  <Tag
                    color={statusColor}
                    size="small"
                    style={{ marginLeft: '8px', color: 'white' }}
                  >
                    {parameterStatus}
                  </Tag>
                )}
              </Text>
              <Tooltip title={
                isUsingDynamicRange
                  ? `Dynamic range: ${activeRange.min}-${activeRange.max} ${config.unit} (Ideal: ${dynamicRange.idealTarget} ${config.unit})`
                  : `Normal range: ${normalRange.min || '?'}-${normalRange.max || '?'} ${config.unit}`
              }>
                <InfoCircleOutlined style={{ marginLeft: '8px', color: '#8c8c8c' }} />
              </Tooltip>
              {config.affects && (
                <Tag color="blue" size="small" style={{ marginLeft: '8px' }}>
                  Affects {config.affects.length}
                </Tag>
              )}
            </Col>
            <Col>
              <InputNumber
                size="small"
                value={inputValue}
                min={activeRange.min}
                max={activeRange.max}
                step={config.range.step}
                onChange={handleInputChange}
                style={{ width: '80px' }}
                status={valueStatus.includes('critical') ? 'error' : valueStatus === 'normal' ? '' : 'warning'}
              />
              <Text type="secondary" style={{ marginLeft: '8px' }}>
                {config.unit}
              </Text>
            </Col>
          </Row>
        </Col>
        
        <Col span={24}>
          <div style={{
            position: 'relative',
            paddingTop: '20px', // Extra space for enhanced visual indicators
            paddingBottom: '10px'
          }}>
            <Slider
              value={inputValue}
              min={activeRange.min}
              max={activeRange.max}
              step={config.range.step}
              onChange={handleSliderChange}
              marks={marks}
              tooltip={{
                formatter: (val) => `${val} ${config.unit}`,
                placement: 'top'
              }}
              styles={{
                track: { backgroundColor: statusColors[valueStatus] },
                handle: { borderColor: statusColors[valueStatus] },
                mark: {
                  fontSize: '11px',
                  fontWeight: 'bold'
                },
                markLabel: {
                  fontSize: '10px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }
              }}
            />
          </div>
        </Col>
        
        {isAffected && (
          <Col span={24}>
            <Alert
              message={
                <Text style={{ fontSize: '12px' }}>
                  <ThunderboltOutlined style={{ color: '#52c41a' }} />
                  {' '}Affected by {cascadingChange.sourceParameter}: 
                  {cascadingChange.changeAmount > 0 ? ' +' : ' '}
                  {typeof cascadingChange.changeAmount === 'number' ? cascadingChange.changeAmount.toFixed(2) : '0.00'} {config.unit}
                  {' '}({typeof cascadingChange.changePercent === 'number' ? cascadingChange.changePercent.toFixed(1) : '0.0'}%)
                </Text>
              }
              type="success"
              showIcon={false}
              style={{ padding: '4px 8px' }}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
};

/**
 * Ordinal Parameter Control Component
 * For rating scales and ordered categories (e.g., sleep quality, nutrition score)
 */
export const OrdinalParameterControl = ({ 
  paramKey, 
  config, 
  value, 
  onChange, 
  cascadingChange = null 
}) => {
  const handleChange = useCallback((newValue) => {
    onChange(paramKey, newValue);
  }, [paramKey, onChange]);
  
  const isAffected = !!cascadingChange;
  
  // Determine if this is a 1-5 scale or custom range
  const isRatingScale = config.range.min === 1 && config.range.max === 5 && config.range.step === 0.5;
  
  if (isRatingScale) {
    return (
      <Card 
        size="small" 
        style={{ 
          marginBottom: '16px',
          border: isAffected ? '2px solid #52c41a' : '1px solid #d9d9d9',
          backgroundColor: isAffected ? '#f6ffed' : 'white'
        }}
      >
        <Row gutter={[16, 8]} align="middle">
          <Col span={24}>
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong>
                  {getParameterDisplayName(paramKey) || config.displayName || paramKey}
                </Text>
                {config.affects && (
                  <Tag color="blue" size="small" style={{ marginLeft: '8px' }}>
                    Affects {config.affects.length}
                  </Tag>
                )}
              </Col>
              <Col>
                <Text strong style={{ fontSize: '16px' }}>
                  {typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value} / 5
                </Text>
              </Col>
            </Row>
          </Col>
          
          <Col span={24}>
            <Rate
              value={typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value}
              onChange={handleChange}
              allowHalf
              count={5}
              style={{ fontSize: '24px' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                1 = Poor, 2 = Fair, 3 = Good, 4 = Very Good, 5 = Excellent
              </Text>
            </div>
          </Col>
          
          {isAffected && (
            <Col span={24}>
              <Alert
                message={
                  <Text style={{ fontSize: '12px' }}>
                    <ThunderboltOutlined style={{ color: '#52c41a' }} />
                    {' '}Affected by {cascadingChange.sourceParameter}
                  </Text>
                }
                type="success"
                showIcon={false}
                style={{ padding: '4px 8px' }}
              />
            </Col>
          )}
        </Row>
      </Card>
    );
  }
  
  // For non-rating ordinal scales (e.g., days per week)
  return (
    <Card 
      size="small" 
      style={{ 
        marginBottom: '16px',
        border: isAffected ? '2px solid #52c41a' : '1px solid #d9d9d9',
        backgroundColor: isAffected ? '#f6ffed' : 'white'
      }}
    >
      <Row gutter={[16, 8]} align="middle">
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <Text strong>
                {getParameterDisplayName(paramKey) || config.displayName || paramKey}
              </Text>
              {config.affects && (
                <Tag color="blue" size="small" style={{ marginLeft: '8px' }}>
                  Affects {config.affects.length}
                </Tag>
              )}
            </Col>
            <Col>
              <Text strong style={{ fontSize: '16px' }}>
                {typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value} {config.unit}
              </Text>
            </Col>
          </Row>
        </Col>
        
        <Col span={24}>
          <Slider
            value={typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value}
            min={config.range.min}
            max={config.range.max}
            step={config.range.step}
            onChange={handleChange}
            marks={{
              [config.range.min]: config.range.min,
              [Math.floor((config.range.min + config.range.max) / 2)]: 'Moderate',
              [config.range.max]: config.range.max
            }}
            tooltip={{ 
              formatter: (val) => `${val} ${config.unit}`,
              placement: 'top'
            }}
          />
        </Col>
        
        {config.categories && (
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
              {Object.entries(config.categories).map(([category, range]) => (
                <Text 
                  key={category}
                  type={
                    value >= (range.min || config.range.min) && 
                    value <= (range.max || config.range.max) ? 'default' : 'secondary'
                  }
                  style={{ 
                    fontWeight: value >= (range.min || config.range.min) && 
                               value <= (range.max || config.range.max) ? 'bold' : 'normal'
                  }}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </Text>
              ))}
            </div>
          </Col>
        )}
      </Row>
    </Card>
  );
};

/**
 * Categorical Parameter Control Component
 * For discrete options (e.g., physical exam findings, imaging results)
 */
export const CategoricalParameterControl = ({ 
  paramKey, 
  config, 
  value, 
  onChange 
}) => {
  const handleChange = useCallback((newValue) => {
    onChange(paramKey, newValue);
  }, [paramKey, onChange]);
  
  return (
    <Card size="small" style={{ marginBottom: '16px' }}>
      <Row gutter={[16, 8]} align="middle">
        <Col span={24}>
          <Text strong>
            {getParameterDisplayName(paramKey) || config.displayName || paramKey}
          </Text>
        </Col>
        
        <Col span={24}>
          <Select
            value={typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value}
            onChange={handleChange}
            style={{ width: '100%' }}
            placeholder="Select option..."
          >
            {config.options.map(option => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
    </Card>
  );
};

/**
 * Binary Parameter Control Component
 * For yes/no or present/absent parameters
 */
export const BinaryParameterControl = ({ 
  paramKey, 
  config, 
  value, 
  onChange 
}) => {
  const handleChange = useCallback((checked) => {
    const newValue = checked ? config.options[0] : config.options[1];
    onChange(paramKey, newValue);
  }, [paramKey, config.options, onChange]);
  
  const extractedValue = typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value;
  const isChecked = extractedValue === config.options[0];
  
  return (
    <Card size="small" style={{ marginBottom: '16px' }}>
      <Row gutter={[16, 8]} align="middle">
        <Col span={18}>
          <Text strong>
            {getParameterDisplayName(paramKey) || config.displayName || paramKey}
          </Text>
        </Col>
        
        <Col span={6} style={{ textAlign: 'right' }}>
          <Switch
            checked={isChecked}
            onChange={handleChange}
            checkedChildren={config.options[0]}
            unCheckedChildren={config.options[1]}
          />
        </Col>
      </Row>
    </Card>
  );
};

/**
 * Derived Parameter Display Component
 * For calculated parameters (read-only)
 */
export const DerivedParameterDisplay = ({ 
  paramKey, 
  config, 
  value, 
  dependencies = {} 
}) => {
  const getStatusColor = (val) => {
    if (config.normalRange) {
      if (val < config.normalRange.min || val > config.normalRange.max) {
        return '#faad14'; // Warning
      }
    }
    if (config.categories) {
      // Find which category this value falls into
      for (const [category, range] of Object.entries(config.categories)) {
        if (val >= (range.min || 0) && val <= (range.max || Infinity)) {
          if (category === 'normal') return '#52c41a';
          if (category === 'overweight' || category === 'underweight') return '#faad14';
          if (category === 'obese') return '#ff4d4f';
        }
      }
    }
    return '#52c41a'; // Default to normal
  };
  
  const statusColor = getStatusColor(value);
  
  return (
    <Card 
      size="small" 
      style={{ 
        marginBottom: '16px',
        border: `2px solid ${statusColor}`,
        backgroundColor: '#fafafa'
      }}
    >
      <Row gutter={[16, 8]} align="middle">
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <Text strong>
                <CalculatorOutlined style={{ marginRight: '8px', color: statusColor }} />
                {getParameterDisplayName(paramKey) || config.displayName || paramKey}
              </Text>
              <Tooltip title={`Calculated from: ${config.dependencies?.join(', ')}`}>
                <InfoCircleOutlined style={{ marginLeft: '8px', color: '#8c8c8c' }} />
              </Tooltip>
            </Col>
            <Col>
              <Statistic
                value={typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value}
                suffix={config.unit}
                precision={2}
                valueStyle={{
                  color: statusColor,
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              />
            </Col>
          </Row>
        </Col>
        
        {config.categories && (
          <Col span={24}>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              <Text>Categories: </Text>
              {Object.entries(config.categories).map(([category, range], index) => (
                <span key={category}>
                  {index > 0 && ' | '}
                  <Text 
                    style={{ 
                      color: value >= (range.min || 0) && value <= (range.max || Infinity) ? statusColor : '#8c8c8c',
                      fontWeight: value >= (range.min || 0) && value <= (range.max || Infinity) ? 'bold' : 'normal'
                    }}
                  >
                    {category}: {range.min || '0'}-{range.max || '∞'}
                  </Text>
                </span>
              ))}
            </div>
          </Col>
        )}
        
        {config.formula && (
          <Col span={24}>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Formula: {config.formula}
            </Text>
          </Col>
        )}
      </Row>
    </Card>
  );
};

/**
 * Main Parameter Control Factory
 * Routes to appropriate control based on parameter type
 */
export const ParameterControl = (props) => {
  const { config } = props;
  
  switch (config.type) {
    case PARAMETER_TYPES.CONTINUOUS:
      return <ContinuousParameterControl {...props} />;
    case PARAMETER_TYPES.ORDINAL:
      return <OrdinalParameterControl {...props} />;
    case PARAMETER_TYPES.CATEGORICAL:
      return <CategoricalParameterControl {...props} />;
    case PARAMETER_TYPES.BINARY:
      return <BinaryParameterControl {...props} />;
    case PARAMETER_TYPES.DERIVED:
      return <DerivedParameterDisplay {...props} />;
    default:
      return <ContinuousParameterControl {...props} />;
  }
};

export default {
  ParameterControl,
  ContinuousParameterControl,
  OrdinalParameterControl,
  CategoricalParameterControl,
  BinaryParameterControl,
  DerivedParameterDisplay
};
